/**
 * Running Timetable Set client — a named pattern of running days per season
 * (/api/research/running-sets, db/76, §5d). Applying a set to a session gives the
 * season roll-up its REAL running-day count per day-class (never an assumed one).
 */
import type { RunningDay, RunningTimetableSetFull, RunningTimetableSetMeta } from './types'

const JSON_HEADERS = { 'content-type': 'application/json' }

async function readJson(r: Response): Promise<Record<string, unknown>> {
  return (await r.json().catch(() => ({}))) as Record<string, unknown>
}
function fail(d: Record<string, unknown>, r: Response, what: string): Error {
  return new Error(typeof d.error === 'string' ? d.error : `${what} failed (${r.status})`)
}

export async function listRunningSets(season?: string): Promise<RunningTimetableSetMeta[]> {
  const q = season ? `?season=${encodeURIComponent(season)}` : ''
  const r = await fetch(`/api/research/running-sets${q}`)
  const d = await readJson(r)
  if (!r.ok) throw fail(d, r, 'list running sets')
  return (d.sets as RunningTimetableSetMeta[]) ?? []
}

export interface LoadedRunningSet extends RunningTimetableSetMeta { days: RunningDay[] }

export async function loadRunningSet(id: number): Promise<LoadedRunningSet> {
  const r = await fetch(`/api/research/running-sets/${id}`)
  const d = await readJson(r)
  if (!r.ok) throw fail(d, r, 'load running set')
  const s = d.set as RunningTimetableSetFull
  return { ...s, days: parseDays(s.days_json) }
}

export interface RunningSetInput { name: string; season: string; days: RunningDay[] }

export async function createRunningSet(input: RunningSetInput): Promise<number> {
  const r = await fetch('/api/research/running-sets', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(input) })
  const d = await readJson(r)
  if (!r.ok) throw fail(d, r, 'create running set')
  return d.id as number
}

export async function updateRunningSet(id: number, input: RunningSetInput): Promise<void> {
  const r = await fetch(`/api/research/running-sets/${id}`, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(input) })
  if (!r.ok) throw fail(await readJson(r), r, 'update running set')
}

export async function deleteRunningSet(id: number): Promise<void> {
  const r = await fetch(`/api/research/running-sets/${id}`, { method: 'DELETE' })
  if (!r.ok && r.status !== 204) throw fail(await readJson(r), r, 'delete running set')
}

/** Mark one set the season's default (or clear it) — it then loads automatically. */
export async function setDefaultRunningSet(id: number, isDefault: boolean): Promise<void> {
  const r = await fetch(`/api/research/running-sets/${id}`, {
    method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ default: isDefault }),
  })
  if (!r.ok) throw fail(await readJson(r), r, 'set default running set')
}

function parseDays(json: string): RunningDay[] {
  try { const a = JSON.parse(json); return Array.isArray(a) ? (a as RunningDay[]) : [] } catch { return [] }
}

/** Running-day count per day_class — what the season roll-up multiplies each slice by. */
export function runningDayCounts(days: RunningDay[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const d of days) if (d.running) out[d.day_class] = (out[d.day_class] ?? 0) + 1
  return out
}
